import { createRoot, hydrateRoot } from 'react-dom/client';
import '@/index.css';
import { initI18n, loadAndAddLocale } from '@/shared/i18n/config';
import App from '@/App';
import { reportWebVitals } from '@/shared/utils/reportWebVitals';

const rootElement = document.getElementById('root');

if (!rootElement) {
  document.body.textContent = 'Error: Root element not found!';
} else {
  // i18n init is synchronous (no network) — translations load async after
  // mount. Every t() call already passes a fallback string, so the brief
  // window where the bundle hasn't landed yet still renders correct copy.
  // This shaves ~300-500ms off React mount on slow 4G by removing the
  // 49KB en.json fetch from the critical render path.
  initI18n();

  // If the Express BFF served a pre-rendered variant, the <html> element
  // carries data-prerendered="1". In that case we MUST use hydrateRoot
  // instead of createRoot — otherwise React throws away the pre-painted
  // DOM (defeating the LCP win) and renders from scratch.
  // For all other routes (non-landing pages still ship via dist/index.html)
  // we createRoot as before, since the splash is the only DOM there.
  const isPrerendered =
    document.documentElement.getAttribute('data-prerendered') === '1';

  // Splash removal is handled by the CSS animation (compositor thread,
  // fires regardless of JS work) PLUS the rAF cleanup at the bottom of
  // this file. We used to ALSO remove the splash synchronously here
  // ("safety net"), but that ran too early — main.tsx executes shortly
  // after vendor-react.js, before Chrome has had a chance to commit a
  // paint frame for the splash. The early removal would hide the splash
  // BEFORE FCP could fire on it, costing us the FCP win we set out to
  // gain. The CSS animation now waits 1500 ms to give Chrome time to
  // paint + register FCP first.

  // Capture and report hydration errors in production so a regression
  // here surfaces in our analytics. Without this, a hydration mismatch
  // would silently fall back to client-rendering (defeating the LCP win)
  // with no visible warning unless the user opens DevTools.
  const onHydrationError = (err: unknown) => {
    // Push a structured marker to dataLayer (GTM) for downstream alerting.
    // Falls back to console.error so dev environments still see it loudly.
    try {
      const msg = err instanceof Error ? err.message : String(err);
      const payload = {
        event: 'hydration_error',
        error_message: msg.slice(0, 500),
        page: window.location.pathname,
        prerendered: '1',
      };
      ((window as Window & { dataLayer?: unknown[] }).dataLayer ||= []).push(payload);
      console.error('[hydration_error]', err);
    } catch {
      console.error('[hydration_error]', err);
    }
  };

  if (isPrerendered) {
    hydrateRoot(rootElement, <App />, {
      onCaughtError: onHydrationError,
      onUncaughtError: onHydrationError,
      onRecoverableError: onHydrationError,
    });
  } else {
    const root = createRoot(rootElement);
    root.render(<App />);
  }

  // Fetch the locale bundle in the background and merge it in once it
  // arrives. i18next triggers a re-render of all <Trans>/useTranslation
  // consumers automatically when addResourceBundle is called.
  loadAndAddLocale();

  // Splash dismissal strategy:
  //
  //   Pre-rendered visitors: dismiss IMMEDIATELY. The hero <img> is
  //     already in the served HTML — the splash overlay sitting on top
  //     of it would block Chrome from registering the hero as the LCP
  //     element until dismissal. By dismissing on the FIRST animation
  //     frame, Chrome sees the hero as soon as it paints (~600 ms on
  //     Slow 4G) instead of waiting until hydration finishes (~3-5 s).
  //
  //   SPA-shell visitors (no pre-render): keep the splash visible until
  //     React hydrates. Without it, the user would see a blank screen
  //     during the ~2-3 s of JS parse + React mount on slow mobile.
  //
  //   Pre-render capture mode (window.__PRERENDERING set by
  //     scripts/prerender.mjs): never dismiss — the splash element MUST
  //     be present in the captured HTML for FCP on real visitors.
  const isPrerenderingCapture =
    typeof window !== 'undefined' && (window as Window & { __PRERENDERING?: boolean }).__PRERENDERING;
  if (!isPrerenderingCapture) {
    const dismissSplash = () => {
      const splash = document.getElementById('midash-splash');
      if (!splash) return;
      splash.classList.add('midash-splash--dismissed');
      setTimeout(() => splash.remove(), 350);
    };
    if (isPrerendered) {
      // Hero is already painted under the splash; remove the cover ASAP
      // so Chrome can score LCP on the hero img.
      requestAnimationFrame(dismissSplash);
    } else {
      // SPA mode — wait until React has mounted + painted once.
      requestAnimationFrame(() => requestAnimationFrame(dismissSplash));
    }
  }
}

// Report Core Web Vitals for performance monitoring
reportWebVitals();
